import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('isAuthenticated');
      window.location.href = '/auth';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },
  
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
  
  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      // Ignore errors on logout
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');
  },
  
  // Get current user's assigned surveys (for doctors)
  getMySurveys: async () => {
    const response = await api.get('/users/me/surveys');
    return response.data;
  },

  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token, password) => {
    const response = await api.post('/auth/reset-password', { token, password });
    return response.data;
  },
};

// Users API (Admin only)
export const usersAPI = {
  getAll: async () => {
    const response = await api.get('/users/');
    return response.data;
  },
  
  getById: async (userId) => {
    const response = await api.get(`/users/${userId}`);
    return response.data;
  },
  
  create: async (userData) => {
    const response = await api.post('/users/', userData);
    return response.data;
  },
  
  update: async (userId, userData) => {
    const response = await api.put(`/users/${userId}`, userData);
    return response.data;
  },
  
  delete: async (userId) => {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
  },
  
  activate: async (userId) => {
    const response = await api.post(`/users/${userId}/activate`);
    return response.data;
  },
};

// Surveys API
export const surveysAPI = {
  getAll: async (params = {}) => {
    const response = await api.get('/surveys/', { params });
    return response.data;
  },
  
  getById: async (surveyId) => {
    const response = await api.get(`/surveys/${surveyId}`);
    return response.data;
  },
  
  create: async (surveyData) => {
    const response = await api.post('/surveys/', surveyData);
    return response.data;
  },
  
  update: async (surveyId, surveyData) => {
    const response = await api.put(`/surveys/${surveyId}`, surveyData);
    return response.data;
  },
  
  delete: async (surveyId) => {
    const response = await api.delete(`/surveys/${surveyId}`);
    return response.data;
  },
  
  addResponse: async (surveyId) => {
    const response = await api.post(`/surveys/${surveyId}/response`);
    return response.data;
  },
  
  // Survey Users
  getUsers: async (surveyId) => {
    const response = await api.get(`/surveys/${surveyId}/users`);
    return response.data;
  },
  
  assignUsers: async (surveyId, userIds) => {
    const response = await api.post(`/surveys/${surveyId}/assign-users`, { user_ids: userIds });
    return response.data;
  },
  
  // Survey Analytics
  getAnalytics: async (surveyId) => {
    const response = await api.get(`/surveys/${surveyId}/analytics`);
    return response.data;
  },
  
  // Export
  exportReport: async (surveyId) => {
    const response = await api.get(`/surveys/${surveyId}/export/report`, { responseType: 'blob' });
    return response.data;
  },
  
  exportJson: async (surveyId) => {
    const response = await api.get(`/surveys/${surveyId}/export/json`, { responseType: 'blob' });
    return response.data;
  },
  
  // Consent Form
  getConsent: async (surveyId) => {
    const response = await api.get(`/surveys/${surveyId}/consent`);
    return response.data;
  },
  
  updateConsent: async (surveyId, consentData) => {
    const response = await api.put(`/surveys/${surveyId}/consent`, consentData);
    return response.data;
  },
  
  // Thank You Message
  getThankYou: async (surveyId) => {
    const response = await api.get(`/surveys/${surveyId}/thank-you`);
    return response.data;
  },
  
  updateThankYou: async (surveyId, thankYouData) => {
    const response = await api.put(`/surveys/${surveyId}/thank-you`, thankYouData);
    return response.data;
  },
  
  // Public Survey (for doctors)
  getPublicSurvey: async (surveyId) => {
    const response = await api.get(`/surveys/${surveyId}/public`);
    return response.data;
  },
  
  // Submit Survey Response
  submitResponse: async (surveyId, submissionData) => {
    const response = await api.post(`/surveys/${surveyId}/submit`, submissionData);
    return response.data;
  },
  
  // Get Survey Responses
  getResponses: async (surveyId) => {
    const response = await api.get(`/surveys/${surveyId}/responses`);
    return response.data;
  },
  
  exportBankingDetails: async (surveyId, completedOnly = true) => {
    const response = await api.get(`/surveys/${surveyId}/export/banking-details`, {
      params: { completed_only: completedOnly },
      responseType: 'blob'
    });
    return response;
  },

  exportDocumentsZip: async (surveyId) => {
    const response = await api.get(`/surveys/${surveyId}/export/documents-zip`, {
      responseType: 'blob'
    });
    return response;
  },

  getAnalysis: async (surveyId) => {
    const response = await api.get(`/surveys/${surveyId}/analysis`);
    return response.data;
  },

  getAuditLogs: async (surveyId) => {
    const response = await api.get(`/surveys/${surveyId}/audit-logs`);
    return response.data;
  },

  getEmailStatus: async (surveyId) => {
    const response = await api.get(`/surveys/${surveyId}/email-status`);
    return response.data;
  },

  sendMassEmail: async (surveyId, subject) => {
    const response = await api.post(`/surveys/${surveyId}/send-mass-email`, { subject });
    return response.data;
  },

  bulkUploadUsers: async (surveyId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/surveys/${surveyId}/bulk-upload-users`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  getBulkUploadTemplate: (surveyId) => {
    return `${API}/surveys/${surveyId}/bulk-upload-template`;
  },
};

// Files API
export const filesAPI = {
  upload: async (docType, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/files/upload/${docType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  
  uploadForUser: async (docType, username, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/files/upload/${docType}/for-user/${username}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  
  getDownloadUrl: (userId, filename) => {
    return `${API}/files/${userId}/${filename}`;
  },

  download: async (url) => {
    // Handle both relative (/api/files/...) and full URLs
    const path = url.startsWith('http') ? url.replace(BACKEND_URL, '') : url;
    const cleanPath = path.startsWith('/api') ? path.replace('/api', '') : path;
    const response = await api.get(cleanPath, { responseType: 'blob' });
    const filename = cleanPath.split('/').pop();
    const blobUrl = window.URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(blobUrl);
  }
};

// Dashboard API
export const dashboardAPI = {
  getStats: async () => {
    const response = await api.get('/dashboard/stats');
    return response.data;
  },
  
  getActivity: async (limit = 10) => {
    const response = await api.get('/dashboard/activity', { params: { limit } });
    return response.data;
  },
};

export default api;
